import { dateFormatServer, useToggle } from '@alluradirect/react';
import { SelectedDateRange } from 'components/views/pages/Checkout/CheckoutDateRangePicker/useDateRangePicker';
import { CheckoutDateRangePicker } from 'components/views/pages/Checkout/CheckoutDateRangePicker/CheckoutDateRangePicker';
import { BookingSettingsItem } from 'components/views/pages/Checkout/CheckoutStep1/components/BookingSettingsItem';
import { CheckoutParamEnum } from 'components/views/pages/Checkout/types';
import { useRouter } from 'next/router';
import { generateDatesDisplay } from 'components/views/pages/Checkout/helpers';
import { usePropertyUnavailableDates } from '../../hooks/usePropertyUnavilableDates';
import { useCheckoutParams } from '../../hooks/useCheckoutParams';
import { LoadingIndicator } from 'components/core/LoadingIndicator/LoadingIndicator';
import { usePropertyCalendarDates } from 'hooks/api/property/infiniteQueries/usePropertyCalendarDates';

interface Props {
  startDate: string | undefined;
  endDate: string | undefined;
  isInvalidDates: boolean;
  title?: string | undefined;
  placeholder?: string | undefined;
  datesToggle: ReturnType<typeof useToggle>;
}

export const BookingSettingsDates = (props: Props) => {
  const router = useRouter();
  const datesToggle = props.datesToggle;

  const { propertyId } = useCheckoutParams();

  const datesDisplay = generateDatesDisplay({
    startDate: props.startDate,
    endDate: props.endDate,
  }, props.placeholder);

  const unavailableDatesQuery = usePropertyUnavailableDates(Number(propertyId), {
    enabled: Boolean(propertyId),
    hasToastsEnabled: false,
  });

  const calendarDatesQuery = usePropertyCalendarDates(Number(propertyId), "", Boolean(propertyId));
  const ratesData = calendarDatesQuery?.data?.pages?.map(page => page.dates)?.flat()?.reduce((acc, item) => {
    const { DATE, SEASON } = item ?? {};
    if (!DATE || !SEASON) return acc;

    const rate = Number(SEASON?.RATES?.[0]?.RATE);
    if (!rate) return acc;

    return {
      ...acc,
      [DATE]: `$${rate.toFixed(0)}`
    };
  }, {});

  if (!unavailableDatesQuery) return <LoadingIndicator />;

  const onSaveDates = (selectedDateRange: SelectedDateRange) => {
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          [CheckoutParamEnum.startDate]: selectedDateRange.from ? dateFormatServer(selectedDateRange.from) : '',
          [CheckoutParamEnum.endDate]: selectedDateRange.to ? dateFormatServer(selectedDateRange.to) : '',
          [CheckoutParamEnum.allura_offer_code]: undefined,
          [CheckoutParamEnum.allura_offer_id]: undefined,
          [CheckoutParamEnum.owner_offer_code]: undefined,
          [CheckoutParamEnum.owner_offer_id]: undefined,
        },
      },
      undefined,
      {
        shallow: true,
        scroll: false,
      },
    );
    datesToggle.close();
  };

  const initialStartDate = props.isInvalidDates ? undefined : props.startDate;
  const initialEndDate = props.isInvalidDates ? undefined : props.endDate;

  return (
    <>

      <BookingSettingsItem
        title={props.title ?? 'Dates'}
        value={datesDisplay}
        onClick={datesToggle.open}
        disabled={!unavailableDatesQuery.data}
        isHighlighted={!initialStartDate}
      />


      {/* Dates Modal */}
      {datesToggle.isOpen && unavailableDatesQuery.data ? (
        <CheckoutDateRangePicker
          startDate={initialStartDate}
          endDate={initialEndDate}
          isOpen={datesToggle.isOpen}
          close={datesToggle.close}
          lastBookableDate={unavailableDatesQuery.data.DATA.LAST_BOOKABLE_DATE}
          lastDateOfRates={unavailableDatesQuery.data.DATA.LAST_DATE_OF_RATES}
          unavailabilityArrival={unavailableDatesQuery.data.DATA.UNAVAILABILITY_ARRIVAL}
          unavailabilityDeparture={unavailableDatesQuery.data.DATA.UNAVAILABILITY_DEPARTURE}
          minNightsDates={unavailableDatesQuery.data.DATA.MIN_NIGHTS_DATES}
          onSaveDates={onSaveDates}
          ratesData={ratesData}
        />
      ) : null}
    </>
  );
};
